/**
 * Date and value formatting helpers for chart tooltips and axis labels.
 * Uses moment-compatible format strings (not Highcharts % tokens).
 */
import {DataGranularity, HistoryMetric} from '../../api/types';
import * as Dates from '../Dates';
import {DateTimeConfig} from '../Dates';
import {isRainMetric} from '../Generic/RainMetricUtils';
import * as Units from '../Units';

// Every date format below follows the date and time preferences (FE #428):
// times on the user's clock, dates through the moment locale's own long forms,
// which `Dates.setLocale` makes day-first for English when the user chose
// DD/MM. Hard-coded day-first and 24-hour forms showed a New York user "24.
// Oct" and "02:00". The fallback is `lll` with the user's clock: a bare
// `lll` is 12-hour in English whatever the clock (FE #438).

/**
 * Returns a moment-compatible date format string for tooltip headers
 * based on the data granularity.
 */
export const getTooltipDateFormat = (
  granularity: DataGranularity,
  dateTimeConfig?: DateTimeConfig,
): string => {
  switch (granularity) {
    case 'max':
    case 'H':
    case '3H':
    case '6H':
      return `${Dates.clockFormat(dateTimeConfig)} (${Dates.weekdayDateFormat()})`;
    case 'D':
      return Dates.weekdayDateFormat();
    case 'W':
      return 'LL';
    case 'M':
      return 'MMMM YYYY';
    default:
      return Dates.dateTimeFormat(dateTimeConfig);
  }
};

/**
 * Returns a moment-compatible date format for x-axis tick labels. The chart
 * renderers use it only when they do not compute their own ticks; otherwise
 * `formatTimeTick` decides from the tick step.
 */
export const getAxisDateFormat = (
  granularity: DataGranularity,
  dateTimeConfig?: DateTimeConfig,
): string => {
  switch (granularity) {
    case 'max':
    case 'H':
    case '3H':
    case '6H':
      return Dates.clockFormat(dateTimeConfig);
    case 'D':
    case 'W':
      return Dates.dayMonthFormat();
    case 'M':
      return 'MMM';
    default:
      return Dates.dateTimeFormat(dateTimeConfig);
  }
};

const DAY_SECONDS = 86400;
const MONTH_SECONDS = 2592000;

/** The day and short month of a timestamp, in the user's date order. */
export const formatDayMonth = (timestamp: number, timezone: string) =>
  Dates.formatDate(timestamp, timezone, Dates.dayMonthFormat());

/**
 * The label of a datetime axis tick, from the step between ticks: month and
 * year for monthly ticks, day and month for daily ones, otherwise the time on
 * the user's clock. A 12-hour tick drops its minutes only when they are 0 in
 * `timezone`: the ticks fall on whole UTC hours, which are 1:30 PM in India.
 */
export const formatTimeTick = (
  timestamp: number,
  timezone: string,
  stepSeconds: number,
  dateTimeConfig?: DateTimeConfig,
): string => {
  if (stepSeconds >= MONTH_SECONDS) {
    return Dates.formatDate(timestamp, timezone, "MMM 'YY");
  }
  if (stepSeconds >= DAY_SECONDS) {
    return formatDayMonth(timestamp, timezone);
  }
  const local = Dates.timezoneDateFromTimestamp(timestamp, timezone);
  if (dateTimeConfig && !dateTimeConfig.is_24_hour) {
    return local.format(
      Dates.twelveHourFormat({minutes: local.minute() !== 0}),
    );
  }
  return local.format('HH:mm');
};

/**
 * Format a unix timestamp (seconds) into a human-readable string.
 */
export const formatTimestamp = (
  timestamp: number,
  timezone: string,
  format: string,
): string => {
  return Dates.formatDate(timestamp, timezone, format);
};

/**
 * Get the unit label string for a given metric.
 */
export const getMetricUnitLabel = (
  metric: string,
  units: Units.Units,
): string => {
  return Units.getUnitLabel(units, metric);
};

/**
 * Convert a raw metric value using the user's preferred units.
 */
export const convertMetricValue = (
  value: number | null | undefined,
  metric: string,
  units: Units.Units,
  precision?: number,
): number | null => {
  if (value === null || value === undefined) return null;
  return Units.convertValue(value, metric, units, precision);
};

/**
 * Returns the default decimal precision for a given metric type.
 * This matches the existing ChartUtils.getDataTypePrecision.
 */
export const getMetricPrecision = (
  metric: string,
  units?: Units.Units,
): number => {
  // When units are provided, use unit-aware precision from the mapping
  if (units) {
    const unitPrecision = Units.getPrecision(metric, units);
    if (unitPrecision !== null) return unitPrecision;
  }

  // Fallback: static precision when units are not available
  switch (metric) {
    case 'T':
      return 1;
    case 'U':
      return 0;
    case 'SLP':
      return 1;
    case 'RR_1H':
    case 'RR_3H':
    case 'RR_6H':
    case 'RR_12H':
    case 'RR_24H':
      return 1;
    case 'SOLAR_RADIATION':
      return 0;
    case 'UV':
      return 1;
    case 'VIS':
      return 1;
    case 'FF':
    case 'FXY':
      return 0;
    case 'AQI':
      return 0;
    case 'LIGHTNING_STRIKE_COUNT':
      return 0;
    default:
      return 0;
  }
};

/**
 * Format a numeric value with the appropriate precision for its metric.
 */
export const formatMetricValue = (
  value: number,
  metric: string,
  units: Units.Units,
  precision?: number,
): string => {
  const converted = Units.convertValue(value, metric, units, precision);
  if (converted === null) return '-';
  const p = precision ?? getMetricPrecision(metric, units);
  return converted.toFixed(p);
};

/**
 * Maps a HistoryMetric to the base metric type used for unit conversion.
 * E.g. T_MIN, T_MAX -> 'T' for temperature conversion.
 */
export const getConversionMetric = (metric: HistoryMetric): string => {
  if (metric.startsWith('T_') || metric === 'T') return 'T';
  if (
    metric === 'HEAT_INDEX' ||
    metric === 'HUMIDEX' ||
    metric === 'WIND_CHILL' ||
    metric === 'THSW' ||
    metric === 'THW'
  )
    return 'T';
  if (metric.startsWith('U_') || metric === 'U') return 'U';
  if (metric.startsWith('SLP_') || metric === 'SLP') return 'SLP';
  if (isRainMetric(metric) || metric === 'ET') return 'RR_1H';
  if (metric === 'DD' || metric === 'DXY') return metric; // direction angles, no conversion
  if (metric === 'FF' || metric === 'FXY') return 'FF';
  if (metric.startsWith('SOLAR_RADIATION') || metric === 'SOLAR_RADIATION')
    return 'SOLAR_RADIATION';
  if (metric === 'SOLAR_ENERGY') return 'SOLAR_ENERGY';
  if (metric.startsWith('UV') || metric === 'UV') return 'UV';
  if (metric.startsWith('VIS') || metric === 'VIS') return 'VIS';
  if (metric.startsWith('AQI') || metric === 'AQI') return 'AQI';
  if (metric === 'WIND_RUN') return 'WIND_RUN';
  return metric;
};
